import {
    ASSIGNMENT_LIST_MIN_HEIGHT,
    ASSIGNMENT_LIST_TRANSITION_MS,
} from 'components/Navigation/assignmentListScrollConstants';
import { useCallback, useEffect, useRef, useState } from 'react';

const useAssignmentListScrollMode = ({ enabled, open }) => {
    const [isWholeNavScrollable, setIsWholeNavScrollable] = useState(false);
    const assignmentListRef = useRef(null);
    const navigationTopRef = useRef(null);

    const measureAssignmentList = useCallback(() => {
        const assignmentList = assignmentListRef.current;
        if (assignmentList) setIsWholeNavScrollable(assignmentList.clientHeight < ASSIGNMENT_LIST_MIN_HEIGHT);
    }, []);

    useEffect(() => {
        if (!enabled || !open) {
            setIsWholeNavScrollable(false);
            return undefined;
        }
        if (isWholeNavScrollable) return undefined;

        let hasSettled = false;
        const settleTimer = window.setTimeout(() => {
            hasSettled = true;
            measureAssignmentList();
        }, ASSIGNMENT_LIST_TRANSITION_MS);
        const resizeObserver =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(() => hasSettled && measureAssignmentList());
        if (resizeObserver && assignmentListRef.current) resizeObserver.observe(assignmentListRef.current);

        return () => {
            window.clearTimeout(settleTimer);
            resizeObserver?.disconnect();
        };
    }, [enabled, isWholeNavScrollable, measureAssignmentList, open]);

    useEffect(() => {
        if (!isWholeNavScrollable) return undefined;

        const navigationTop = navigationTopRef.current;
        const initialHeight = navigationTop?.clientHeight;
        const reevaluate = () => setIsWholeNavScrollable(false);
        const resizeObserver =
            navigationTop && typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => {
                      if (navigationTop.clientHeight !== initialHeight) reevaluate();
                  })
                : null;
        resizeObserver?.observe(navigationTop);
        window.addEventListener('resize', reevaluate);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', reevaluate);
        };
    }, [isWholeNavScrollable]);

    return {
        assignmentListRef,
        isWholeNavScrollable,
        navigationTopRef,
    };
};

export default useAssignmentListScrollMode;
